import { Card, H1, H2, ListRow, Separator } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';

import { RecurringTransactionsDisclosure } from './RecurringTransactionsDisclosure';

import { trackClick } from '../../../features/track';
import { PayrollContributionConfigFragment } from '../../../graphql/fragments/payrollContributionConfig.generated';
import { RecurringTransactionConfigFragment } from '../../../graphql/fragments/recurringTransactionConfig.generated';
import { PurposeRecurringTransactionConfigsQuery } from '../../../purpose/components/recurring-transactions-card/graphql/purposeRecurringTransactionConfigs.generated';
import { urlBuilder } from '../../urlBuilder';
import MessageBox from '../message-box/message-box';
import { PayrollConfigsList } from '../payroll-contribution-config/PayrollConfigsList';
import { RecurringTransactionsConfig } from '../recurring-transactions-config/RecurringTransactionsConfig';
import { RecurringTransactionsSetup } from '../recurring-transactions-config/RecurringTransactionsSetup';

type Envelope = PurposeRecurringTransactionConfigsQuery['purpose']['envelope'];
type LegalSubAccount = Extract<Envelope['legalSubAccounts'][number], { __typename: 'CashAccount' }>;
type Disclosure = LegalSubAccount['recurringDepositMotivation'];

type RecurringTransactionsCardViewProps = {
  recurringTransactionConfigs: RecurringTransactionConfigFragment[];
  isWithdrawal: (sourceLegacySubAccountId: number) => boolean;
  payrollContributionConfigs?: PayrollContributionConfigFragment[];
  recurringTransactionGuidance?: Envelope['recurringTransactionMessages'];
  disclosures?: Disclosure;
  subAccountId: string;
};

function getLegalAccountId(config: PayrollContributionConfigFragment) {
  if (config.account.__typename === 'ManagedSubAccount') {
    return config.account.legalAccount.legacyId;
  }
  return null;
}

function hasAny401kConfigs(configs: PayrollContributionConfigFragment[]) {
  return configs.some(config => config.account.__typename === 'ManagedSubAccount');
}

export const RecurringTransactionsCardView = ({
  recurringTransactionConfigs,
  isWithdrawal,
  recurringTransactionGuidance = [],
  payrollContributionConfigs = [],
  disclosures,
  subAccountId,
}: RecurringTransactionsCardViewProps) => {
  const shouldShowRecurringTransactionSetup = !payrollContributionConfigs.length && !recurringTransactionConfigs.length;
  const shouldShowSavingsHeader = !!(disclosures && disclosures.shortDisclosure && disclosures.longDisclosure);

  const legalAccountIdValue =
    payrollContributionConfigs.length > 0 ? getLegalAccountId(payrollContributionConfigs[0]) : null;

  const deferralSettingEditUrl = legalAccountIdValue
    ? urlBuilder.appDeferralSetting.absolute({
        legalAccountId: String(legalAccountIdValue),
        return_to: window.location.pathname,
      })
    : undefined;

  return (
    <Card>
      <Card.Header>
        <H2 variant="title200" css={{ padding: 'space.300' }}>
          {shouldShowRecurringTransactionSetup ? 'Automate your savings' : 'Recurring transactions'}
        </H2>
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content>
        {shouldShowRecurringTransactionSetup ? (
          <>
            <RecurringTransactionsSetup subAccountId={subAccountId} subText={disclosures?.description} />
            {shouldShowSavingsHeader && (
              <RecurringTransactionsDisclosure
                shortDisclosure={disclosures.shortDisclosure}
                longDisclosure={disclosures.longDisclosure}
              />
            )}
          </>
        ) : (
          <>
            {payrollContributionConfigs.length > 0 && (
              <>
                {hasAny401kConfigs(payrollContributionConfigs) && (
                  <ListRow css={{ paddingTop: 'space.300', paddingBottom: 'space.0' }}>
                    <ListRow.LeadingContent>
                      <H1 variant="title100">401(k) contribution settings</H1>
                    </ListRow.LeadingContent>
                    {deferralSettingEditUrl && (
                      <ListRow.TrailingContent>
                        <SambaAutoLink
                          to={deferralSettingEditUrl}
                          onClick={() => trackClick({ name: 'DeferralSettingEditIntended' })}
                        >
                          Edit
                        </SambaAutoLink>
                      </ListRow.TrailingContent>
                    )}
                  </ListRow>
                )}
                <PayrollConfigsList configs={payrollContributionConfigs} />
              </>
            )}

            {recurringTransactionConfigs.length > 0 && (
              <>
                {payrollContributionConfigs.length > 0 && (
                  <>
                    <Separator css={{ fullWidthWithInset: 'space.300' }} />
                    <ListRow css={{ paddingTop: 'space.300', paddingBottom: 'space.0' }}>
                      <ListRow.LeadingContent>
                        <H1 variant="title100">Other transactions</H1>
                      </ListRow.LeadingContent>
                    </ListRow>
                  </>
                )}
                {recurringTransactionConfigs.map(config => (
                  <RecurringTransactionsConfig key={config.id} config={config} isWithdrawal={isWithdrawal} />
                ))}
              </>
            )}
          </>
        )}
      </Card.Content>
      {recurringTransactionGuidance?.length > 0 && (
        <Card.Footer
          css={{
            marginX: 'space.300',
            marginBottom: 'space.300',
          }}
        >
          <MessageBox messages={recurringTransactionGuidance} location="envelope_overview_recurring_transactions" />
        </Card.Footer>
      )}
    </Card>
  );
};
